import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@shared/ui';
import { useState } from 'react';
import { AdminCalendarChangeAggregate } from './AdminCalendarChangeAggregate';
import { AdminCalendarChangeReviewQueue } from './AdminCalendarChangeReviewQueue';
import { AdminCalendarChangeRuntimeControl } from './AdminCalendarChangeRuntimeControl';
import { useGetCalendarChangesQuery } from './api/adminCalendarChangesApi';
import type { CalendarChangeScope, CalendarChangeState } from './types';

const scopes: CalendarChangeScope[] = [
  'this_occurrence',
  'all_events',
  'this_and_following',
  'occurrence_cancelled',
  'series_cancelled',
  'pending_correlation',
  'ambiguous',
];

const states: CalendarChangeState[] = [
  'observed',
  'converged',
  'review_required',
  'pending_correlation',
  'resolved',
];

const dateTime = (value?: string) => (value ? new Date(value).toLocaleString() : 'Unknown');

type CalendarChangeFilters = {
  coachPersonId: string;
  scope: CalendarChangeScope | '';
  state: CalendarChangeState | '';
  reason: string;
  from: string;
  to: string;
};

const emptyFilters: CalendarChangeFilters = {
  coachPersonId: '',
  scope: '',
  state: '',
  reason: '',
  from: '',
  to: '',
};

export function AdminCalendarChangesPage() {
  const [draftFilters, setDraftFilters] = useState<CalendarChangeFilters>(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState<CalendarChangeFilters>(emptyFilters);
  const [cursor, setCursor] = useState<string | null>(null);
  const query = useGetCalendarChangesQuery({
    coachPersonId: appliedFilters.coachPersonId || undefined,
    scope: appliedFilters.scope || undefined,
    state: appliedFilters.state || undefined,
    reason: appliedFilters.reason || undefined,
    from: appliedFilters.from ? `${appliedFilters.from}T00:00:00Z` : undefined,
    to: appliedFilters.to ? `${appliedFilters.to}T23:59:59.999999999Z` : undefined,
    cursor,
    limit: 50,
  });

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setCursor(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography component="h1" variant="h5">
        Calendar changes
      </Typography>
      <AdminCalendarChangeRuntimeControl />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(6, 1fr)' },
          gap: 1.5,
        }}
      >
        <TextField
          label="Coach person ID"
          value={draftFilters.coachPersonId}
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, coachPersonId: event.target.value }))
          }
          size="small"
        />
        <TextField
          label="Scope"
          value={draftFilters.scope}
          onChange={(event) =>
            setDraftFilters((current) => ({
              ...current,
              scope: event.target.value as CalendarChangeScope | '',
            }))
          }
          select
          size="small"
        >
          <MenuItem value="">All scopes</MenuItem>
          {scopes.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="State"
          value={draftFilters.state}
          onChange={(event) =>
            setDraftFilters((current) => ({
              ...current,
              state: event.target.value as CalendarChangeState | '',
            }))
          }
          select
          size="small"
        >
          <MenuItem value="">All states</MenuItem>
          {states.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Reason"
          value={draftFilters.reason}
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, reason: event.target.value }))
          }
          size="small"
        />
        <TextField
          label="From"
          type="date"
          value={draftFilters.from}
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, from: event.target.value }))
          }
          size="small"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To"
          type="date"
          value={draftFilters.to}
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, to: event.target.value }))
          }
          size="small"
          InputLabelProps={{ shrink: true }}
        />
      </Box>
      <Button onClick={applyFilters} sx={{ alignSelf: 'flex-start' }} variant="contained">
        Apply filters
      </Button>
      {query.error ? (
        <Alert role="alert" severity="error">
          Could not load calendar changes.
        </Alert>
      ) : query.isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Detected</TableCell>
                  <TableCell>Coach</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell align="right">Affected</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data?.changes.map((change) => (
                  <TableRow key={change.id}>
                    <TableCell>
                      <Link href={`/admin/calendar-changes/${encodeURIComponent(change.id)}`}>
                        {dateTime(change.detected_at)}
                      </Link>
                    </TableCell>
                    <TableCell>{change.coach_person_id}</TableCell>
                    <TableCell>{change.scope}</TableCell>
                    <TableCell>{change.state}</TableCell>
                    <TableCell>{`${dateTime(change.prior_start_at)} -> ${dateTime(change.resulting_start_at)}`}</TableCell>
                    <TableCell align="right">{change.affected_records.length}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {query.data?.changes.length === 0 && <Typography>No calendar changes found.</Typography>}
          {query.data?.next_cursor && (
            <Button
              onClick={() => setCursor(query.data?.next_cursor ?? null)}
              sx={{ alignSelf: 'flex-end' }}
              variant="outlined"
            >
              Next page
            </Button>
          )}
        </>
      )}
      <AdminCalendarChangeReviewQueue />
      <AdminCalendarChangeAggregate />
    </Box>
  );
}
